import { useCallback, useMemo } from 'react';

import { isAxiosError } from '@mint-lib/api';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useQueryClient } from '@mint-lib/query-client';
import { KnowledgeBase } from '@mint-sdk/api-ai-assistant';

import {
  canMoveInto,
  canMoveSource,
  type MoveSource,
  type MoveTarget,
  type MoveRejection,
} from '../../helpers/knowledge-base/fileSystemMove.js';
import { isFolderNode } from '../../helpers/knowledge-base/fileSystemNode.js';
import { moveListedNodes } from '../../queries/knowledgeBase.js';
import type { FileSystemNode } from '../../types/fileSystem.js';

const getErrorDetail = (error: unknown): string | undefined => {
  if (!isAxiosError<unknown>(error)) return undefined;

  const data = error.response?.data;

  if (typeof data !== 'object' || !data || !('detail' in data)) {
    return undefined;
  }

  return typeof data.detail === 'string' && data.detail
    ? data.detail
    : undefined;
};

type MoveResult = { moved: boolean; reason?: MoveRejection };

export const useMoveNodes = ({ currentFolderId, access }: MoveSource) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const queryClient = useQueryClient();
  const { mutateAsync: relocateFolder } = KnowledgeBase.moveFolder();
  const { mutateAsync: relocateFile } = KnowledgeBase.moveFile();

  const source = useMemo<MoveSource>(
    () => ({ currentFolderId, access }),
    [currentFolderId, access],
  );

  const canMoveFrom = useCallback(
    (node: FileSystemNode) => canMoveSource(node, source).allowed,
    [source],
  );

  const checkMove = useCallback(
    (nodes: FileSystemNode[], target: MoveTarget) =>
      canMoveInto(nodes, target, source),
    [source],
  );

  const relocate = useCallback(
    async (nodes: FileSystemNode[], targetFolderId: string) => {
      try {
        await Promise.all(
          nodes.map((node) =>
            isFolderNode(node)
              ? relocateFolder({
                  urlParams: { folderId: node.id },
                  data: { newParentFolderId: targetFolderId },
                })
              : relocateFile({
                  urlParams: { fileId: node.id },
                  data: { newFolderId: targetFolderId },
                }),
          ),
        );
      } catch (error) {
        void KnowledgeBase.getKnowledgeBaseListing.invalidateAll(
          queryClient,
          {},
        );

        enqueueSnackbar({
          variant: 'error',
          subTitle: getErrorDetail(error) ?? t('Some items could not be moved'),
          removable: true,
        });

        return false;
      }

      moveListedNodes(
        queryClient,
        { from: currentFolderId, to: targetFolderId },
        new Set(nodes.map((node) => node.id)),
      );

      return true;
    },
    [queryClient, currentFolderId, relocateFolder, relocateFile, t],
  );

  const moveNodes = useCallback(
    async (
      nodes: FileSystemNode[],
      target: MoveTarget,
    ): Promise<MoveResult> => {
      const verdict = checkMove(nodes, target);

      if (!verdict.allowed) return { moved: false, reason: verdict.reason };

      const moved = await relocate(nodes, target.folderId);

      return { moved };
    },
    [checkMove, relocate],
  );

  return { checkMove, canMoveFrom, moveNodes };
};
